import React from 'react';
import { TypeBRenderer } from '@/src/modules/renderer/TypeBRenderer';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { transliterateToMarathi } from '@/src/infrastructure/lib/transliterate';
import manifest from './manifest.json';

const META: TemplateMetadata = {
  id: 'marathi-wedding-classic',
  name: 'Marathi Wedding Classic',
  occasionType: 'WEDDING',
  description: 'A beautiful traditional Marathi wedding template generated with Type B pipeline.',
  isPremium: true,
  tags: ['marathi', 'traditional', 'type-b'],
  colorScheme: { primary: '#D32F2F', accent: '#FFB300', bg: '#FFFDE7' },
};

export function MarathiWeddingClassic({ data }: { data: InvitationData }) {
  const groom = data.customData?.wedding?.groom;
  const bride = data.customData?.wedding?.bride;

  const brideFather = bride?.fatherName ? transliterateToMarathi(bride.fatherName) : '';
  const brideMother = bride?.motherName ? transliterateToMarathi(bride.motherName) : '';
  const brideParents = brideFather
    ? `सौ. ${brideMother} व श्री. ${brideFather}\nयांची कन्या`
    : 'सौ. सचिता व श्री. गोरक्षनाथ मार्तंड पा. खुले\nरा. कोल्हेवाडी ता. संगमनेर जि.अ.नगर यांची कन्या';

  const groomFather = groom?.fatherName ? transliterateToMarathi(groom.fatherName) : '';
  const groomMother = groom?.motherName ? transliterateToMarathi(groom.motherName) : '';
  const groomParents = groomFather
    ? `सौ. ${groomMother} व श्री. ${groomFather}\nयांचे चिरंजीव`
    : 'सौ. शोभा व श्री. लहानभाऊ रखमाजी पा. एरंडे\nरा. झोळे ता. संगमनेर जि.अ.नगर यांचे चिरंजीव';

  const brideName = bride?.fullName ? `चि.सौ.कां. ${transliterateToMarathi(bride.fullName)}` : 'चि.सौ.कां. तेजस्वी';
  const groomName = groom?.fullName ? `चि. ${transliterateToMarathi(groom.fullName)}` : 'चि. प्रविण';

  const weddingEvent = data.customData?.wedding?.weddingEvents?.find(
    (e) => e.title.toLowerCase().includes('wedding') || e.title.toLowerCase().includes('विवाह')
  );
  const eventTime = weddingEvent?.startTime || 'दुपारी 12:45 मि';

  let formattedDate = 'बुधवार दि. 18/09/2024\nरोजी दुपारी 12:45 मि';
  if (data.eventDate) {
    try {
      const d = new Date(data.eventDate);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
      const weekdayName = weekdays[d.getDay()];

      let timeStr = eventTime;
      if (timeStr.toLowerCase().includes('pm')) {
        timeStr = timeStr.replace(/pm/i, 'वाजता');
      } else if (timeStr.toLowerCase().includes('am')) {
        timeStr = timeStr.replace(/am/i, 'वाजता');
      }
      timeStr = transliterateToMarathi(timeStr);

      formattedDate = `${weekdayName} दि. ${day}/${month}/${year}\nरोजी ${timeStr}`;
    } catch (e) {
      // Keep default on error
    }
  }

  const haldiEvent = data.customData?.wedding?.weddingEvents?.find(
    (e) =>
      e.title.toLowerCase().includes('haldi') ||
      e.title.toLowerCase().includes('हळद') ||
      e.title.toLowerCase().includes('reception') ||
      e.title.toLowerCase().includes('स्वागत')
  );
  const receptionTitle = haldiEvent ? transliterateToMarathi(haldiEvent.title) : 'हळद समारंभ';
  let receptionDate = 'मंगळवार दि. 13 मे 2025 सायं. 06.00 वा.';
  if (haldiEvent?.date) {
    try {
      const d = new Date(haldiEvent.date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
      const weekdayName = weekdays[d.getDay()];
      const timeStr = haldiEvent.startTime ? transliterateToMarathi(haldiEvent.startTime) : 'सायं. 06.00 वा.';
      receptionDate = `${weekdayName} दि. ${day}/${month}/${year} ${timeStr}`;
    } catch (e) {
      // Keep default on error
    }
  }

  const venueAddress = data.eventVenue ? transliterateToMarathi(data.eventVenue) : 'ढोले पाटील लॉन्स, राजापूर रोड\nता. संगमनेर जि. अहिल्यानगर';
  const familyNote = data.hostName
    ? `निमंत्रक\n${transliterateToMarathi(data.hostName)}\nसमस्त परिवार`
    : 'निमंत्रक\nश्री. गोरक्षनाथ मार्तंड पा. खुले\nसमस्त खुले पाटील परिवार';

  // Ensure Ganesha shloka is always present at the top
  // Use eventName as fallback if customMessage is not provided
  const coupleNames = `|| श्री गणेशाय नमः ||\n${
    data.customMessage && data.customMessage !== 'A heartfelt message to your guests goes here.'
      ? transliterateToMarathi(data.customMessage)
      : data.eventName
      ? transliterateToMarathi(data.eventName)
      : 'स.न.वि.वि आमच्या येथे श्रीहरी कृपेने'
  }`;

  const textValues = {
    coupleNames,
    bride_name: brideName,
    bride_details: brideParents,
    groom_name: groomName,
    groom_details: groomParents,
    wedding_title: 'यांचा शुभविवाह समारंभ',
    wedding_date: formattedDate,
    wedding_note: 'या शुभ मुहूर्तावर करण्याचे योजिले आहे',
    reception_title: receptionTitle,
    reception_date: receptionDate,
    ceremony_title: 'विवाहस्थळ',
    venue_address: venueAddress,
    family_note: familyNote,
  };

  // Modify manifest in-memory to hide gibberish layers and fix text slot layouts
  const modifiedManifest = React.useMemo(() => {
    // 1. Filter out layers containing pre-baked gibberish English text
    const filteredLayers = manifest.layers.filter(
      (l) => l.id !== 'wedding-text-block' && l.id !== 'event-details' && l.id !== 'decorative-elements'
    );

    // 2. Map of optimized coordinates and font sizes for a clean vertical flow layout (1080x1920 canvas)
    const slotConfigs: Record<string, { y: number; fontSize: number; fontWeight?: string; color?: string }> = {
      coupleNames: { y: 15, fontSize: 32, fontWeight: 'normal' },
      bride_name: { y: 24, fontSize: 44, fontWeight: 'bold' },
      bride_details: { y: 29, fontSize: 26 },
      groom_name: { y: 36, fontSize: 44, fontWeight: 'bold' },
      groom_details: { y: 41, fontSize: 26 },
      wedding_title: { y: 48, fontSize: 32 },
      wedding_date: { y: 55, fontSize: 28, fontWeight: 'bold', color: '#D32F2F' },
      wedding_note: { y: 60, fontSize: 24 },
      reception_title: { y: 66, fontSize: 28, fontWeight: 'bold' },
      reception_date: { y: 70, fontSize: 26 },
      ceremony_title: { y: 76, fontSize: 28, fontWeight: 'bold' },
      venue_address: { y: 80, fontSize: 26 },
      family_note: { y: 87, fontSize: 24 },
    };

    const updatedTextSlots = manifest.textSlots.map((slot) => {
      const config = slotConfigs[slot.key];
      if (config) {
        return {
          ...slot,
          y: config.y,
          fontSize: config.fontSize,
          fontWeight: config.fontWeight || slot.fontWeight,
          color: config.color || slot.color,
        };
      }
      return slot;
    });

    return {
      ...manifest,
      layers: filteredLayers,
      textSlots: updatedTextSlots,
    };
  }, []);

  return (
    <TypeBRenderer
      manifest={modifiedManifest as any}
      textValues={textValues}
      className="bg-[#FFFDE7]"
    />
  );
}

registry.register({ metadata: META, component: MarathiWeddingClassic });
